import React from "react";
import PropTypes from "prop-types";

import TimeAgo from "../TimeAgo/TimeAgo";

interface ChatBubbleProps {
    isReply?: boolean,
    isAssistant?: boolean,
    isError?: boolean,
    isLoading?: boolean,
    name?: string,
    image: React.ReactNode,
    dateTime?: string,
    actions?: React.ReactNode,
    statusMessage?: string,
    // Baseline arguments
    children?: React.ReactNode,
    className?: string;
}

/**
 * An entry into a chat.
 * 
 * ## _
 * ### Arguments
 * @param {boolean} isReply - A flag to set if the message is a reply.
 * @param {boolean} isAssistant - A flag to set if the message is from an assistant.
 * @param {boolean} isError - A flag to set if the message is an error.
 * @param {boolean} isLoading - A flag to set if the message is loading.
 * @param {string} name - The name attributed to the sender of the message.
 * @param {React.ReactNode} image - The icon for the sender of the message.
 * @param {string} dateTime - The ISO6801 timestamp for the time of the message being sent.
 * @param {string} statusMessage - An optional field for denoting a status for the message (e.g. "Loading...")
 * @param {React.ReactNode} actions - The buttons or icons that the bubble should do.
 * @param {React.ReactNode} children - The contents inside the message.
 * @param {string} className - CSS classes to apply to the message.
 * 
 * ## _
 * ### Functions
 * @param {function} onClick - Function called when the chat bubble is clicked.  
 */
class ChatBubble extends React.Component<ChatBubbleProps> {
    static propTypes = {
        isReply: PropTypes.bool,
        isAssistant: PropTypes.bool,
        isError: PropTypes.bool,
        isLoading: PropTypes.bool,
        name: PropTypes.string,
        image: PropTypes.node,
        dateTime: PropTypes.string,
        actions: PropTypes.node,
        statusMessage: PropTypes.string,
        children: PropTypes.node,
        className: PropTypes.string,
    };

    static defaultProps = {
        size: "medium",
        className: "",
        isLoading: false,
    };

    render() {
        const { isReply, isAssistant, isError, isLoading, name, image, dateTime, actions, statusMessage, children, className, ...rest } = this.props;

        let classes = "m-chat-bubble"

        if (isReply) classes += ` m-chat-bubble--reply`
        if (isAssistant) classes += ` m-chat-bubble--ai`
        if (isError) classes += ` m-chat-bubble--error`

        if (className) classes += ` ${className}`

        // Making the avatar and message separate components. Replies have the order swapped, so it's easier to do this.
        const BubbleAvatar = () => {
            return (
                <div className="m-chat-bubble__avatar-wrapper">
                    <div className="a-avatar">
                        <div className="a-avatar__img a-avatar__img--lottie">
                            {image}
                        </div>
                    </div>
                </div>
            );
        }

        const BubbleMessage = () => {
            return (
                <div className="m-chat-bubble__message-wrapper">   
                    <div className="m-chat-bubble__message-inner">
                        <div className="m-chat-bubble__message">
                            { isLoading ? 
                                <>
                                    <div className="a-skeleton a-skeleton--line l-mb-xs"></div>
                                    <div className="a-skeleton a-skeleton--line l-mb-xs"></div>
                                    <div className="a-skeleton a-skeleton--line l-mb-xs"></div>
                                </>
                            :
                                <>{children}</>
                            }
                        </div>
                        <div className="m-chat-bubble__actions">
                            <div className="row">
                                <div className="col-md-6 h-vert-center-col">
                                    {statusMessage && (
                                        <p className="m-chat-bubble__status-msg">
                                            {statusMessage}
                                        </p>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <div className="m-icon-row m-icon-row--right-align m-icon-row--condensed">
                                        {actions}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="m-chat-bubble__meta-wrapper">
                        {/* The picture is on either side based on the reply status. Not doing the separate constants because this is a oneliner. */}
                        { isReply && <p className="m-chat-bubble__speaker">{name}</p> }
                        <p className="m-chat-bubble__timestamp">
                            {dateTime && <TimeAgo timestamp={dateTime} limit={3} limitUnit="day"/>}
                        </p>
                        { !isReply && <p className="m-chat-bubble__speaker">{name}</p> }
                    </div>
                </div>
            );
        }

        return (
            <div className={classes} {...rest}>
                { isReply ?
                    <>
                        <BubbleAvatar />
                        <BubbleMessage />
                    </> :
                    <>
                        <BubbleMessage />
                        <BubbleAvatar />
                    </>  
                }
            </div>
        );
    }
}

export default ChatBubble;